import { clamp } from 'es-toolkit/compat'

import type { LGraphNode } from '@/lib/litegraph/src/LGraphNode'
import { LiteGraph } from '@/lib/litegraph/src/litegraph'
import type {
  IComboWidget,
  IStringComboWidget
} from '@/lib/litegraph/src/types/widgets'
import { warnDeprecated } from '@/lib/litegraph/src/utils/feedback'
import {
  getAvailableImages,
  hasImageFiles
} from '@/services/imageSelectorService'

import { BaseSteppedWidget } from './BaseSteppedWidget'
import type { WidgetEventOptions } from './BaseWidget'

/**
 * This is used as an (invalid) assertion to resolve issues with legacy duck-typed values.
 *
 * Function style in use by:
 * https://github.com/kijai/ComfyUI-KJNodes/blob/c3dc82108a2a86c17094107ead61d63f8c76200e/web/js/setgetnodes.js#L401-L404
 */
type Values =
  | string[]
  | Record<string, string>
  | ((widget?: ComboWidget, node?: LGraphNode) => string[])

function toArray(values: Values): string[] {
  return Array.isArray(values) ? values : Object.keys(values)
}

export class ComboWidget
  extends BaseSteppedWidget<IStringComboWidget | IComboWidget>
  implements IComboWidget
{
  override type = 'combo' as const

  override get _displayValue() {
    if (this.computedDisabled) return ''
    const { values: rawValues } = this.options
    if (rawValues) {
      const values = typeof rawValues === 'function' ? rawValues() : rawValues

      if (values && !Array.isArray(values)) {
        return values[this.value]
      }
    }
    return typeof this.value === 'number' ? String(this.value) : this.value
  }

  private getValues(node: LGraphNode): Values {
    const { values } = this.options
    if (values == null) throw new Error('[ComboWidget]: values is required')

    return typeof values === 'function' ? values(this, node) : values
  }

  /**
   * Checks if the value is {@link Array.at at} the given index in the combo list.
   * @param increment `true` if checking the use of the increment button, `false` for decrement
   * @returns `true` if the value is at the given index, otherwise `false`.
   */
  private canUseButton(increment: boolean): boolean {
    const { values } = this.options
    // If using legacy duck-typed method, false is the most permissive return value
    if (typeof values === 'function') return false

    const valuesArray = toArray(values)
    if (!(valuesArray.length > 1)) return false

    // Edge case where the value is both the first and last item in the list
    const firstValue = valuesArray.at(0)
    const lastValue = valuesArray.at(-1)
    if (firstValue === lastValue) return true

    return this.value !== (increment ? lastValue : firstValue)
  }

  /**
   * Returns `true` if the current value is not the last value in the list.
   * Handles edge case where the value is both the first and last item in the list.
   */
  override canIncrement(): boolean {
    return this.canUseButton(true)
  }

  override canDecrement(): boolean {
    return this.canUseButton(false)
  }

  override incrementValue(options: WidgetEventOptions): void {
    this.tryChangeValue(1, options)
  }

  override decrementValue(options: WidgetEventOptions): void {
    this.tryChangeValue(-1, options)
  }

  private tryChangeValue(delta: number, options: WidgetEventOptions): void {
    const values = this.getValues(options.node)
    const indexedValues = toArray(values)

    // avoids double click event
    options.canvas.last_mouseclick = 0

    const foundIndex =
      typeof values === 'object'
        ? indexedValues.indexOf(String(this.value)) + delta
        : // @ts-expect-error handle non-string values
          indexedValues.indexOf(this.value) + delta

    const index = clamp(foundIndex, 0, indexedValues.length - 1)

    const value = Array.isArray(values) ? values[index] : index
    this.setValue(value, options)
  }

  override onClick({ e, node, canvas }: WidgetEventOptions) {
    const x = e.canvasX - node.pos[0]
    const width = this.width || node.size[0]

    // Deprecated functionality (warning as of v0.14.5)
    if (typeof this.options.values === 'function') {
      warnDeprecated(
        'Using a function for values is deprecated. Use an array of unique values instead.'
      )
    }

    // Determine if clicked on left/right arrows
    if (x < 40) return this.decrementValue({ e, node, canvas })
    if (x > width - 40) return this.incrementValue({ e, node, canvas })

    // Otherwise, show dropdown menu
    const values = this.getValues(node)
    const values_list = toArray(values)

    // Check if values contain image files
    if (hasImageFiles(values_list)) {
      // Show image selector dialog
      void this.showImageSelector(values_list, { e, node, canvas })
    } else {
      // Show regular dropdown menu
      const text_values = values != values_list ? Object.values(values) : values
      new LiteGraph.ContextMenu(text_values, {
        scale: Math.max(1, canvas.ds.scale),
        event: e,
        className: 'dark',
        callback: (value: string) => {
          this.setValue(
            values != values_list ? text_values.indexOf(value) : value,
            { e, node, canvas }
          )
        }
      })
    }
  }

  private async showImageSelector(
    values: string[],
    options: WidgetEventOptions
  ) {
    try {
      // 获取可用图片列表
      const availableImages = await getAvailableImages()

      // 过滤出当前widget相关的图片
      const relevantImages = availableImages.filter((img) =>
        values.includes(img.filename)
      )

      // 使用找到的相关图片或所有可用图片
      const imagesToShow =
        relevantImages.length > 0 ? relevantImages : availableImages
      this.createImageSelectorDialog(imagesToShow, options)
    } catch (error) {
      console.error('显示图片选择器失败:', error)
      // 回退到普通下拉菜单
      new LiteGraph.ContextMenu(values, {
        scale: Math.max(1, options.canvas.ds.scale),
        event: options.e,
        className: 'dark',
        callback: (value: string) => {
          this.setValue(value, options)
        }
      })
    }
  }

  private createImageSelectorDialog(
    _images: any[],
    options: WidgetEventOptions
  ) {
    // 使用Vue组件创建图片选择器弹窗
    void this.createVueImageSelectorDialog(options)
  }

  private async createVueImageSelectorDialog(options: WidgetEventOptions) {
    try {
      // 动态导入Vue组件和PrimeVue
      const { createApp } = await import('vue')
      const { default: ImageSelectorDialog } = await import(
        '@/components/common/ImageSelectorDialog.vue'
      )
      const PrimeVue = await import('primevue/config')
      const { createI18n } = await import('vue-i18n')
      const { default: en } = await import('@/locales/en/main.json')
      const { default: zh } = await import('@/locales/zh/main.json')

      // 创建容器元素
      const container = document.createElement('div')
      document.body.appendChild(container)

      // 创建国际化配置，使用全局的main.json文件
      const i18n = createI18n({
        legacy: false,
        locale: 'zh', // 默认中文
        fallbackLocale: 'en',
        messages: {
          zh,
          en
        }
      })

      // 创建Vue应用
      const app = createApp(ImageSelectorDialog, {
        visible: true,
        'onUpdate:visible': (visible: boolean) => {
          if (!visible) {
            app.unmount()
            document.body.removeChild(container)
          }
        },
        onSelect: (filename: string) => {
          this.setValue(filename, options)
          app.unmount()
          document.body.removeChild(container)
        }
      })

      // 配置PrimeVue
      app.use(PrimeVue.default, {
        ripple: false,
        inputStyle: 'outlined'
      })

      app.use(i18n)

      app.mount(container)
    } catch (error) {
      console.error('Failed to load ImageSelectorDialog component:', error)
      // 回退到简单的HTML弹窗
      this.createSimpleImageSelectorDialog([], options)
    }
  }

  private createSimpleImageSelectorDialog(
    images: any[],
    options: WidgetEventOptions
  ) {
    // 创建简单的图片选择器弹窗的HTML结构（作为回退方案）
    const dialog = document.createElement('div')
    dialog.className = 'image-selector-dialog'
    dialog.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
    `

    const content = document.createElement('div')
    content.style.cssText = `
      background: #1a1a1a;
      border-radius: 8px;
      padding: 20px;
      width: 80vw;
      max-width: 1000px;
      min-width: 800px;
      max-height: 80vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
      color: #ffffff;
    `

    const title = document.createElement('h3')
    title.textContent = 'Select Image'
    title.style.cssText = 'margin: 0 0 20px 0; color: #ffffff;'

    const grid = document.createElement('div')
    grid.style.cssText = `
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      gap: 12px;
      margin-bottom: 20px;
      max-height: 60vh;
      overflow-y: auto;
    `

    images.forEach((image) => {
      const item = document.createElement('div')
      item.style.cssText = `
        border: 2px solid #404040;
        border-radius: 8px;
        padding: 8px;
        cursor: pointer;
        transition: all 0.2s;
        text-align: center;
        background-color: #2d2d2d;
      `

      const img = document.createElement('img')
      img.src = image.url
      img.style.cssText =
        'width: 100%; height: 80px; object-fit: cover; border-radius: 4px;'
      img.onerror = () => {
        // 图片加载失败时不做任何处理，让图片保持失败状态
        console.warn('图片加载失败:', img.src)
      }

      const filename = document.createElement('div')
      filename.textContent = image.filename
      filename.style.cssText =
        'font-size: 12px; margin-top: 5px; color: #cccccc; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;'

      item.appendChild(img)
      item.appendChild(filename)

      item.addEventListener('click', () => {
        this.setValue(image.filename, options)
        document.body.removeChild(dialog)
      })

      item.addEventListener('mouseenter', () => {
        item.style.borderColor = '#6366f1'
        item.style.backgroundColor = '#3d3d3d'
        item.style.transform = 'translateY(-2px)'
        item.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.3)'
      })

      item.addEventListener('mouseleave', () => {
        item.style.borderColor = '#404040'
        item.style.backgroundColor = '#2d2d2d'
        item.style.transform = 'translateY(0)'
        item.style.boxShadow = 'none'
      })

      grid.appendChild(item)
    })

    const buttons = document.createElement('div')
    buttons.style.cssText =
      'display: flex; justify-content: flex-end; gap: 10px;'

    const cancelBtn = document.createElement('button')
    cancelBtn.textContent = 'Cancel'
    cancelBtn.style.cssText = `
      padding: 8px 16px;
      border: 1px solid #404040;
      background: #2d2d2d;
      color: #ffffff;
      border-radius: 4px;
      cursor: pointer;
      transition: all 0.2s;
    `
    cancelBtn.addEventListener('click', () => {
      document.body.removeChild(dialog)
    })

    cancelBtn.addEventListener('mouseenter', () => {
      cancelBtn.style.backgroundColor = '#3d3d3d'
      cancelBtn.style.borderColor = '#4d4d4d'
    })

    cancelBtn.addEventListener('mouseleave', () => {
      cancelBtn.style.backgroundColor = '#2d2d2d'
      cancelBtn.style.borderColor = '#404040'
    })

    buttons.appendChild(cancelBtn)
    content.appendChild(title)
    content.appendChild(grid)
    content.appendChild(buttons)
    dialog.appendChild(content)

    // 点击背景关闭
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog) {
        document.body.removeChild(dialog)
      }
    })

    document.body.appendChild(dialog)
  }
}
